import { useEffect, useState, useCallback, useRef } from 'react';
import { planRowDate } from '../utils/planRowDate';
import { useFavorites } from '../contexts/FavoritesContext';
import MealImage from '../components/MealImage';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Calendar, Sparkles, Check, AlertTriangle, RotateCcw, X, ChefHat, Package, Edit2, Wand2, Filter, Tag, ChevronDown, ChevronUp, Lock, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import BottomNav from '../components/BottomNav';
import TodaySummaryCard from '../components/TodaySummaryCard';
import AiLogJobsSection from '../components/AiLogJobsSection';
import PlanFoodLauncher from '../components/PlanFoodLauncher';
import MealCard from '../components/MealCard';
import { PairSwapDialog } from '../components/PairSwapDialog';
import { buildRegenSelection, isLinkedRow } from '../utils/regenSelection';
import { classifyAutoRegenResult, firstIncompleteReason, retainedOnlyMessage } from '../utils/regenResultClassifier';
import { friendlyRegenerateReasonText } from './planReviewRegenerateDayFailure';
import { recordTasteSignal, recordRegenTasteSignals, TASTE_ACTIONS } from '../utils/tasteSignals';
import CookingPreferencesSelector from '../components/CookingPreferencesSelector';
import PantrySelector from '../components/PantrySelector';
import useUserStore from '../store/userStore';
import { nutritionAPI, profileAPI, mealPlanAPI, mealAPI, smartFillAPI, pantryAPI } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { trackLogFoodAdd, trackLogFoodRemove, trackMealRemoveFromDay, trackPlanGenerateStart, trackPlanGenerateSuccess, trackPlanGenerateFail } from '@/lib/posthog';
import { useSubscriptionContext } from '../contexts/SubscriptionContext';
import { FEATURES } from '../services/subscriptionService';
import { useToday } from '../hooks/useToday';
import { buildUrl, startFlowState } from '../utils/planLocation';
import { useLatestRequest } from '../utils/requestLifecycle';
import { useScreenPerf } from '../utils/perf';
import LoadErrorCard from '../components/LoadErrorCard';
import { mealFromPlanRow } from '../utils/planRowMeal';

// Background enrichment: per-meal details (image, recipe macros). Cards
// already render from the plan rows (getMealWithFallback); each result is
// merged in as it lands, so details already on screen stay visible while a
// refresh runs. Owned by the caller's latest-request lifecycle (`req`).
function hydrateMealDetails(planRows, req, opts, setMealDetails) {
  var uniqueMealIds = [];
  var seen = {};
  for (var i = 0; i < planRows.length; i++) {
    var mealId = planRows[i].meal_id;
    if (mealId && !seen[mealId]) {
      seen[mealId] = true;
      uniqueMealIds.push(mealId);
    }
  }
  uniqueMealIds.forEach(function(id) {
    mealAPI.getMeal(id, opts)
      .then(function(res) {
        if (!req.isCurrent() || !res) return;
        setMealDetails(function(prev) {
          var next = Object.assign({}, prev);
          next[id] = res.data;
          return next;
        });
      })
      .catch(function() { /* card keeps its plan-row fallback */ });
  });
}

// Module-scope snapshot of the last Home content ({ key: user_id|date, plan,
// details }). Only ever read back for the SAME account and day, so a remount
// never shows another user's or another day's plan.
var homeSnapshot = null;

function Home() {
  var navigate = useNavigate();
  var { user, profile, setProfile } = useUserStore();
  var { canAccess, requireFeature, isSubscribed, showPaywall } = useSubscriptionContext();
  // Meal planning (Plan Food / Regenerate / daily plan) is a Fitxess Pro
  // feature. Free users keep logging, macros and progress only.
  // NOTE: the subscription context exposes `isSubscribed` as a BOOLEAN.
  var isPro = isSubscribed;
  // LOCAL current date, auto-refreshed on app-resume / local-midnight so Home
  // never stays stuck on yesterday if left open across midnight.
  var today = useToday();
  // Last Home content for this account + day (see homeSnapshot): a remount
  // (returning from another page) renders it at once and refreshes in place.
  var snapshotKey = (user && user.user_id ? user.user_id : '') + '|' + today;
  var snapshot = homeSnapshot && homeSnapshot.key === snapshotKey ? homeSnapshot : null;
  var [todayPlan, setTodayPlan] = useState(snapshot ? snapshot.plan : []);
  var [mealDetails, setMealDetails] = useState(snapshot ? snapshot.details : {});
  var [loading, setLoading] = useState(!snapshot);
  var [loadError, setLoadError] = useState(null);
  // [PERF] temporary screen timing (MOUNT -> READY).
  useScreenPerf('HOME', !loading, loadError);
  // Latest-request-wins: a newer load (date change / refresh) aborts the older
  // one, which can then never overwrite state; aborted on unmount.
  var homeLoad = useLatestRequest();
  var [actionLoading, setActionLoading] = useState(null);
  var [showPlanLauncher, setShowPlanLauncher] = useState(false);
  var { isFavorite: favHas, toggleFavorite: ctxToggleFavorite } = useFavorites();
  
  // Remove confirmation modal state
  var [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  var [mealToRemove, setMealToRemove] = useState(null);
  
  // Regenerate Today modal state
  var [showRegenerateModal, setShowRegenerateModal] = useState(false);
  // Modal opens directly into the AI flow. The legacy 'confirm' step
  // (Browse vs AI choice) was removed — Regenerate now always means
  // AI regeneration. Users who want manual selection use Plan Food.
  var [regenerateStep, setRegenerateStep] = useState('keep-selection'); // 'keep-selection', 'preferences', 'cooking', 'pantry', 'generating'
  var [regenerating, setRegenerating] = useState(false);
  
  // Keep selections state for regenerate
  var [mealsToKeep, setMealsToKeep] = useState([]); // Array of plan_ids to keep
  var [pairSwapContext, setPairSwapContext] = useState(null); // shared Replace Both dialog
  
  // Cooking preferences state for regenerate
  var [selectedCookware, setSelectedCookware] = useState([]);
  var [selectedCookTime, setSelectedCookTime] = useState(null);
  
  // Pantry state for regenerate
  var [pantryItems, setPantryItems] = useState([]);
  var [fullPantryData, setFullPantryData] = useState({});
  
  // Filters state for regenerate
  var [showFiltersPanel, setShowFiltersPanel] = useState(false);
  var [selectedTags, setSelectedTags] = useState([]);
  var [selectedCuisine, setSelectedCuisine] = useState([]);


  // P8: Favorites are account-backed via FavoritesContext (no localStorage).

  // Bug 9: intermittent blank white band at the top of Home after returning
  // from another page. Root cause: the browser/WebView restores a STALE scroll
  // offset from the previous (taller) route, so Home lands mid-page with an
  // empty band above the content. Force manual scroll restoration and reset to
  // the top whenever Home mounts (both in-app and browser back).
  useEffect(function() {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
  }, []);
  
  // (Cooking preferences + pantry are loaded inside handleOpenRegenerateModal
  // so they're ready by the time the user reaches the 'preferences' step.
  // The legacy useEffect that lazy-loaded on regenerateStep === 'confirm' was
  // removed when the Browse-vs-AI confirm step was retired.)

  // Toggle favorite (account-backed)
  var toggleFavorite = function(mealId, e) {
    if (e) e.stopPropagation();
    ctxToggleFavorite(mealId);
  };

  // Day whose plan/profile are currently on screen (null until the first
  // successful core load). A refetch for the same day refreshes in place.
  var loadedDateRef = useRef(snapshot ? today : null);

  useEffect(function() {
    if (loadedDateRef.current === today) {
      homeSnapshot = { key: snapshotKey, plan: todayPlan, details: mealDetails };
    }
  }, [snapshotKey, today, todayPlan, mealDetails]);

  // Fetch all data for home page
  var fetchData = useCallback(function() {
    console.log('[Home] Fetching data for date:', today);
    var req = homeLoad.begin();
    var opts = { signal: req.signal };
    // Core Home data = daily plan + profile. Only a load with NO usable content
    // for this day shows the Today's Plan spinner; a refetch (return from
    // another page, refresh after an action) keeps the known plan on screen
    // while it refreshes.
    var hasContent = loadedDateRef.current === today;
    if (!hasContent) setLoading(true);
    setLoadError(null);
    
    Promise.all([
      mealPlanAPI.getDailyPlan(today, opts),
      profileAPI.getProfile(opts)
    ])
    .then(function(results) {
      if (!req.isCurrent()) return;
      var planRes = results[0];
      var profileRes = results[1];
      
      console.log('[Home] Daily plan:', planRes.data.length, 'meals');
      
      // Sort meals by meal type order: Breakfast, Lunch, Dinner, Snack
      var mealTypeOrder = { 'Breakfast': 0, 'Lunch': 1, 'Dinner': 2, 'Snack': 3 };
      var sortedPlan = planRes.data.slice().sort(function(a, b) {
        var orderA = mealTypeOrder[a.meal_type] !== undefined ? mealTypeOrder[a.meal_type] : 4;
        var orderB = mealTypeOrder[b.meal_type] !== undefined ? mealTypeOrder[b.meal_type] : 4;
        return orderA - orderB;
      });
      
      setTodayPlan(sortedPlan);
      setProfile(profileRes.data);
      loadedDateRef.current = today;
      // The plan is known: render it now. Meal details below only enrich the
      // cards (image / recipe macros) and never gate the plan section.
      setLoading(false);

      hydrateMealDetails(planRes.data, req, opts, setMealDetails);
    })
    .catch(function(error) {
      if (!req.isCurrent()) return;
      console.error('[Home] Failed to fetch home data:', error);
      // A failed background refresh keeps the plan already on screen; only a
      // load with nothing to show surfaces the recoverable error card.
      if (loadedDateRef.current !== today) setLoadError(error);
      setLoading(false);
    });
  }, [today, setProfile, homeLoad]);

  // Fetch data when component mounts or when returning from other pages
  useEffect(function() {
    fetchData();
  }, [fetchData]);

  var refreshData = function() {
    console.log('[Home] Manual refresh...');
    fetchData();
  };

  // Update meal status (complete/skip/reset)
  var updateMealStatus = function(planId, newStatus, e) {
    e.stopPropagation();
    
    console.log('[Home] Updating meal status:', { planId, newStatus });
    setActionLoading(planId + '-' + newStatus);
    
    // Find meal type for tracking
    var plan = todayPlan.find(function(p) { return p.plan_id === planId; });
    var mealType = plan?.meal_type || 'Unknown';
    
    // Optimistic update
    var previousPlan = todayPlan.slice();
    setTodayPlan(function(current) {
      return current.map(function(p) {
        if (p.plan_id === planId) {
          return Object.assign({}, p, { status: newStatus });
        }
        return p;
      });
    });
    
    mealPlanAPI.updateMealPlan(planId, newStatus)
      .then(function() {
        // Track food logging
        if (newStatus === 'completed') {
          trackLogFoodAdd(mealType);
        }
        // P8b-B1: RECIPE-level Skip of an ACTIVE planned recipe => skip_recipe -1.
        // Not recorded when merely correcting history (already completed/skipped).
        if (newStatus === 'skipped' && plan && plan.meal_id &&
            plan.status !== 'completed' && plan.status !== 'skipped') {
          recordTasteSignal(plan.meal_id, TASTE_ACTIONS.SKIP_RECIPE);
        }
        toast.success('Meal marked as ' + newStatus);
      })
      .catch(function(error) {
        console.error('[Home] Failed to update status:', error);
        setTodayPlan(previousPlan);
        var errorMsg = error.response?.data?.detail || error.message;
        toast.error('Failed: ' + errorMsg);
      })
      .finally(function() {
        setActionLoading(null);
      });
  };

  // Show confirmation modal for removing a skipped meal
  var confirmRemoveSkippedMeal = function(planId, mealName, e) {
    if (e) e.stopPropagation();
    setMealToRemove({ planId: planId, mealName: mealName });
    setShowRemoveConfirm(true);
  };

  // Execute removal after confirmation
  var executeRemoveSkippedMeal = function() {
    if (!mealToRemove) return;
    
    var planId = mealToRemove.planId;
    console.log('[Home] Removing skipped meal:', planId);
    setActionLoading(planId + '-remove');
    setShowRemoveConfirm(false);
    
    // Find meal type for tracking
    var plan = todayPlan.find(function(p) { return p.plan_id === planId; });
    var mealType = plan?.meal_type || 'Unknown';
    
    var previousPlan = todayPlan.slice();
    setTodayPlan(function(current) {
      return current.filter(function(p) { return p.plan_id !== planId; });
    });
    
    mealPlanAPI.deleteMealPlan(planId)
      .then(function() {
        // Track meal removal
        trackMealRemoveFromDay(mealType);
        toast.success('Meal removed from plan');
        setMealToRemove(null);
      })
      .catch(function(error) {
        console.error('[Home] Failed to remove meal:', error);
        setTodayPlan(previousPlan);
        var errorMsg = error.response?.data?.detail || error.message;
        toast.error('Failed: ' + errorMsg);
      })
      .finally(function() {
        setActionLoading(null);
      });
  };

  // Cancel removal
  var cancelRemove = function() {
    setShowRemoveConfirm(false);
    setMealToRemove(null);
  };
  
  // Regenerate Today: open the AI flow directly.
  //
  // The legacy "Browse vs AI" choice modal was removed — Regenerate now
  // always means AI regeneration. Users who want manual selection use
  // Plan Food instead. We pre-load cooking prefs + pantry here so they're
  // available the moment the user reaches the 'preferences' step.
  // Plan Food: visible for every user, but meal planning is Fitxess Pro only —
  // Free users get the paywall and never reach the launcher/generation.
  var handleOpenPlanLauncher = function() {
    if (!isPro) {
      showPaywall(FEATURES.CAN_GENERATE_WEEKLY_PLAN);
      return;
    }
    setShowPlanLauncher(true);
  };

  var handleOpenRegenerateModal = function() {
    // Meal planning (regeneration) is Fitxess Pro only. Free users see the
    // Regenerate control (discoverability) and get the paywall here instead.
    if (!isPro) {
      showPaywall(FEATURES.CAN_REGENERATE_UNLIMITED);
      return;
    }

    profileAPI.getCookingPreferences()
      .then(function(res) {
        var data = res.data;
        setSelectedCookware(data.cookware || []);
        setSelectedCookTime(data.max_cook_time);
      })
      .catch(function(err) {
        console.error('Failed to load cooking preferences:', err);
      });

    pantryAPI.getPantry()
      .then(function(response) {
        var data = response.data || {};
        setFullPantryData(data);
        var allItems = [];
        ['proteins', 'pantry', 'dairy', 'produce', 'other'].forEach(function(cat) {
          var items = data[cat] || [];
          items.forEach(function(item) {
            if (item && item.name && typeof item.name === 'string') {
              allItems.push(item.name);
            }
          });
        });
        setPantryItems(allItems);
      })
      .catch(function(err) {
        console.error('Failed to load pantry:', err);
      });

    // Skip the legacy 'confirm' step entirely. If today already has any
    // still-plannable meals (pending, not yet logged or skipped), route to
    // 'keep-selection' so the user can choose what to keep; if every meal
    // is already completed/skipped, jump straight to 'preferences'.
    var existingMeals = todayPlan.filter(function(p) {
      return p.status !== 'skipped' && p.status !== 'completed';
    });
    setMealsToKeep([]);
    if (existingMeals.length > 0) {
      setRegenerateStep('keep-selection');
    } else {
      setRegenerateStep('preferences');
    }
    setShowRegenerateModal(true);
  };

  var handleCloseRegenerateModal = function() {
    setShowRegenerateModal(false);
    // Reset to the new initial step so a re-open behaves correctly. The
    // open handler always re-decides the actual step based on todayPlan.
    setRegenerateStep('keep-selection');
    setMealsToKeep([]);
  };
  
  // Toggle a meal in the "keep" selection
  var toggleMealToKeep = function(planId) {
    if (mealsToKeep.includes(planId)) {
      setMealsToKeep(mealsToKeep.filter(function(id) { return id !== planId; }));
    } else {
      setMealsToKeep(mealsToKeep.concat([planId]));
    }
  };

  // Open the shared pair-safe Replace Both dialog for a linked row on today.
  // (Single-day view: the partner lives on an adjacent date; the dialog reads
  // the authoritative pair via the backend context.)
  var openHomePairSwap = function(plan, e) {
    if (e) e.stopPropagation();
    if (!plan || !plan.leftover_group_id) return;
    // A leftover shown TODAY has its parent cook on a prior day (finalized/past),
    // so its parent must never be rewritten. Route to the normal single-meal
    // Lunch swap browser, finished by the atomic child-only replacement endpoint.
    if (plan.leftover_role === 'leftover') {
      // Returns straight to Home (not "/", which only reaches Home by
      // bouncing through the login screen's auto-redirect).
      navigate(buildUrl('/meals', {
        guided: 'true', date: today, type: String(plan.meal_type || 'Lunch').toLowerCase(),
        return: '/home', swap: 'true', replacePlanId: plan.plan_id,
        linkedChild: '1', expectedMealId: plan.meal_id || '',
      }), { state: startFlowState() });
      return;
    }
    // A cook row today has its leftover on a future day — pair-safe Replace Both.
    var pairRows = todayPlan
      .filter(function(m) { return m.leftover_group_id && m.leftover_group_id === plan.leftover_group_id; })
      .map(function(m) { return Object.assign({}, m, { date: today }); });
    setPairSwapContext({ groupId: plan.leftover_group_id, planId: plan.plan_id, date: today, pairRows: pairRows });
  };
  
  // Proceed from keep-selection to preferences
  var handleProceedFromKeepSelection = function() {
    setRegenerateStep('preferences');
  };
  
  var handleEditCookingPrefs = function() {
    setRegenerateStep('cooking');
  };
  
  var handleEditPantry = function() {
    setRegenerateStep('pantry');
  };
  
  var handleSavePantry = function(items) {
    setPantryItems(items);
    // PantrySelector handles backend save on Done — just update local state
    setRegenerateStep('preferences');
  };
  
  var handleSaveCookingPrefs = function(cookware, cookTime) {
    setSelectedCookware(cookware);
    setSelectedCookTime(cookTime);
    // Save to backend
    profileAPI.updateCookingPreferences({ cookware: cookware, max_cook_time: cookTime })
      .catch(function(err) {
        console.error('Failed to save cooking preferences:', err);
      });
    setRegenerateStep('preferences');
  };
  
  var handleProceedToGenerate = async function() {
    setRegenerateStep('generating');
    setRegenerating(true);
    trackPlanGenerateStart(1);
    
    try {
      var preferences = {
        fewer_ingredients: false,
        quick_prep: selectedCookTime && selectedCookTime <= 20,
        exclude_types: []
      };
      
      // Pass cooking preferences to smart fill
      var cookingPreferences = {
        cookware: selectedCookware,
        max_cook_time: selectedCookTime
      };
      
      // Build filters object
      var filters = null;
      if (selectedTags.length > 0 || selectedCuisine.length > 0) {
        filters = {};
        if (selectedTags.length > 0) filters.tags = selectedTags;
        if (selectedCuisine.length > 0) filters.cuisine = selectedCuisine;
      }
      
      // Build the regeneration selection via the SHARED helper (identical to
      // PlanningHub) so linked cook/leftover rows are always kept + never
      // excluded, regardless of the user's manual selection.
      var _sel = buildRegenSelection(todayPlan, mealsToKeep);
      var mealsToKeepParam = _sel.mealsToKeep;
      var excludeMealIds = _sel.excludeMealIds;

      console.log('[Regenerate] Calling API with:', {
        dates: [today],
        mealsToKeep: mealsToKeepParam,
        excludeMealIds: excludeMealIds,
        filters: filters
      });

      // DRAFT mode (same flow as Plan's Regenerate): generation never deletes
      // or replaces any saved row. Today only changes when the user taps
      // Confirm in Plan Review (transactional /smart-fill/confirm); Cancel
      // leaves today exactly as it was.
      var res = await smartFillAPI.auto([today], preferences, [], cookingPreferences, filters, mealsToKeepParam, excludeMealIds, true);
      // P8b-B1: a successful draft request records taste signals from the
      // final selection (manual keeps +keep, regenerated-away regulars
      // -regenerate, linked neutral) BEFORE confirmation, exactly as Plan's
      // Regenerate does. They are not reverted if the draft is cancelled.
      if (res && res.data && res.data.success) {
        recordRegenTasteSignals(todayPlan, _sel);
      }

      console.log('[Regenerate] Draft response:', {
        success: res.data.success,
        draft: res.data.draft,
        new_meals_count: res.data.new_meals_count,
        has_changes: res.data.has_changes,
        incomplete_dates: res.data.incomplete_dates,
      });

      // new_meals_count / kept counts are NOT proof today was regenerated: a
      // response can be success:true with today in incomplete_dates. The
      // shared classifier is the only judge of whether there is a complete,
      // reviewable plan for today.
      var classification = classifyAutoRegenResult(res.data, [today]);
      // A constrained day (logged/kept food leaves no room for a suitable
      // addition) is not a completed regeneration, even when has_changes is
      // true because a replaceable pending row exists: never open Review for
      // it, so the saved day is preserved exactly as it is.
      var todayResult = (res.data.daily_results || []).filter(function(r) { return r && r.date === today; })[0];
      var constrainedToday = !!(res.data.success && todayResult
        && todayResult.status === 'constrained_no_feasible_addition'
        && todayResult.compliant === false);

      if (classification.outcome === 'reviewable' && !constrainedToday) {
        trackPlanGenerateSuccess(1, res.data.new_meals_count || 0);
        handleCloseRegenerateModal();
        // Review owns Confirm & Save / Cancel; both return here.
        navigate('/plan/review', { state: startFlowState({
          returnTo: '/home',
          added_plans: res.data.added_plans,
          daily_target: res.data.daily_target || res.data.summary,
          daily_results: res.data.daily_results,
          replace_plan_ids: res.data.replace_plan_ids || [],
          dates: [today],
          goal: res.data.goal,
        }) });
      } else if (classification.outcome === 'no_valid_plan') {
        // No complete replacement for today: apply nothing, stay on Home.
        trackPlanGenerateFail('no_valid_plan');
        toast.error(friendlyRegenerateReasonText(
          firstIncompleteReason(classification),
          "Couldn't find a different valid plan for today. Try removing a filter or excluded meal, then regenerate again."
        ));
        handleCloseRegenerateModal();
      } else if (classification.outcome === 'no_changes' || constrainedToday) {
        // Nothing to add or remove, or a constrained day: informative copy only.
        toast.info(retainedOnlyMessage(res.data.daily_results));
        handleCloseRegenerateModal();
      } else {
        trackPlanGenerateFail('no_meals_generated');
        toast.info('No meals generated');
        handleCloseRegenerateModal();
      }
    } catch (err) {
      console.error('[Regenerate] Error:', err);
      trackPlanGenerateFail(err.response?.data?.detail || 'api_error');
      // These 402s are already surfaced by the global axios interceptor
      // (Paywall / ProUpgradeNotice); close quietly instead of stacking an
      // extra error toast on top (same as Plan's Regenerate).
      var status = err?.response?.status;
      var code = err?.response?.data?.detail?.error || err?.response?.data?.error;
      if (
        status === 402 &&
        (code === 'subscription_required' ||
          code === 'daily_limit_reached' ||
          code === 'planning_around_logged_meals_requires_pro')
      ) {
        handleCloseRegenerateModal();
        return;
      }
      toast.error('Failed to generate plan');
      setRegenerateStep('preferences');
    } finally {
      setRegenerating(false);
    }
  };
  
  // Log Food: the primary Home logging action (LIVE current-day flow). Shared by
  // the primary button and the Free zero-log empty state so both use exactly
  // the same logging navigation.
  var handleLogFoodClick = function() {
    navigate('/log-food?current=1&type=Lunch&return=/home');
  };

  // Add Meal: navigate directly to Log Food page (LIVE current-day flow — no frozen date)
  var handleAddMealClick = function() {
    navigate('/log-food?current=1&return=/home');
  };

  // Remove meal from plan (legacy - used by PlanningHub)
  var removeMeal = function(planId, e) {
    e.stopPropagation();
    
    console.log('[Home] Removing meal:', planId);
    setActionLoading(planId + '-remove');
    
    var previousPlan = todayPlan.slice();
    setTodayPlan(function(current) {
      return current.filter(function(p) { return p.plan_id !== planId; });
    });
    
    mealPlanAPI.deleteMealPlan(planId)
      .then(function() {
        toast.success('Meal removed');
      })
      .catch(function(error) {
        console.error('[Home] Failed to remove meal:', error);
        setTodayPlan(previousPlan);
        var errorMsg = error.response?.data?.detail || error.message;
        toast.error('Failed: ' + errorMsg);
      })
      .finally(function() {
        setActionLoading(null);
      });
  };

  var isActionLoading = function(planId, action) {
    return actionLoading === planId + '-' + action;
  };

  // Separate meals into logged and still planned
  var loggedMeals = todayPlan.filter(function(p) { return p.status === 'completed'; });
  var stillPlannedMeals = todayPlan.filter(function(p) { return p.status !== 'completed' && p.status !== 'skipped'; });
  var skippedMeals = todayPlan.filter(function(p) { return p.status === 'skipped'; });

  // Helper to get meal with fallback to plan data for custom meals
  // Falls back to the plan row (custom/quick entries, AI logs, Spoonacular items) —
  // its macros are the logged values — and always keeps the row's saved image_url.
  var getMealWithFallback = function(plan) {
    return mealFromPlanRow(plan, mealDetails[plan.meal_id], 'Logged Food');
  };

  // Handle meal click - route to custom meal view or regular meal view
  var handleMealClick = function(plan) {
    var mealId = plan.meal_id;
    
    // Build URL params - unified approach for all meal types
    var params = new URLSearchParams();
    params.set('return', '/home');
    // Home always shows the reactive current local day, so a meal opened from Home is a
    // LIVE current-day detail: mark current=1 so a Log Again resolves to today (even across
    // midnight) instead of freezing on the row's stored date. Existing pending rows (planId)
    // still update in place and ignore this.
    params.set('current', '1');
    if (plan.plan_id) params.set('planId', plan.plan_id);
    if (plan.status) params.set('status', plan.status);
    if (plan.meal_type) params.set('type', plan.meal_type);
    params.set('date', planRowDate(plan, today));
    if (plan.meal_name) params.set('name', plan.meal_name);
    
    // Pass macros for custom meals that might not have them in DB
    if (plan.macros) {
      params.set('calories', plan.macros.calories || 0);
      params.set('protein', plan.macros.protein || 0);
      params.set('carbs', plan.macros.carbs || 0);
      params.set('fat', plan.macros.fat || 0);
    }
    
    // Pass scale factor for scaled meals
    if (plan.scale_factor && plan.scale_factor !== 1.0) {
      params.set('scaleFactor', plan.scale_factor);
      if (plan.scale_label) params.set('scaleLabel', plan.scale_label);
    }

    // Grouped custom meals (AI photo logs + multi-food basket logs) open the
    // grouped custom-meal detail screen, which renders combined macros + the
    // individual component rows (via ingredient_details) and supports editing.
    var isGrouped = (
      plan.source === 'AI_PHOTO_LOG' ||
      (typeof mealId === 'string' && (mealId.indexOf('ailog_group_') === 0 || mealId.indexOf('grouped_') === 0)) ||
      (plan.ingredient_count && plan.ingredient_count > 1)
    );
    if (isGrouped) {
      navigate('/meal-view/' + mealId + '?' + params.toString() + '&view=grouped', { state: { mealType: plan.meal_type, grouped: true } });
      return;
    }

    // All other meals go through unified MealView
    navigate('/meal-view/' + mealId + '?' + params.toString(), { state: { mealType: plan.meal_type } });
  };

  // Render meal cards using MealCard component - HOME VARIANT (Log/Undo, Skip, Favorite)
  var loggedCards = loggedMeals.map(function(plan) {
    var meal = getMealWithFallback(plan);
    var mealName = meal ? meal.name : plan.meal_name || 'this meal';
    return (
      <MealCard
        key={plan.plan_id}
        meal={meal}
        planMacros={plan.macros}
        mealType={plan.meal_type}
        planId={plan.plan_id}
        status={plan.status}
        isFavorite={favHas(plan.meal_id)}
        variant="home"
        showLoggedBadge={true}
        onCardClick={function() { handleMealClick(plan); }}
        onLog={function(e) { updateMealStatus(plan.plan_id, 'completed', e); }}
        onUndo={function(e) { updateMealStatus(plan.plan_id, 'pending', e); }}
        onSkip={function(e) { updateMealStatus(plan.plan_id, 'skipped', e); }}
        onRemove={function(e) { confirmRemoveSkippedMeal(plan.plan_id, mealName, e); }}
        onFavorite={function(e) { toggleFavorite(plan.meal_id, e); }}
        isLoading={{
          log: isActionLoading(plan.plan_id, 'completed'),
          undo: isActionLoading(plan.plan_id, 'pending'),
          skip: isActionLoading(plan.plan_id, 'skipped'),
          remove: isActionLoading(plan.plan_id, 'remove'),
          favorite: false
        }}
      />
    );
  });

  var plannedCards = stillPlannedMeals.map(function(plan) {
    var meal = getMealWithFallback(plan);
    return (
      <MealCard
        key={plan.plan_id}
        meal={meal}
        planMacros={plan.macros}
        mealType={plan.meal_type}
        planId={plan.plan_id}
        status={plan.status}
        isFavorite={favHas(plan.meal_id)}
        variant="home"
        showLoggedBadge={false}
        onCardClick={function() { handleMealClick(plan); }}
        onLog={function(e) { updateMealStatus(plan.plan_id, 'completed', e); }}
        onSkip={function(e) { updateMealStatus(plan.plan_id, 'skipped', e); }}
        onFavorite={function(e) { toggleFavorite(plan.meal_id, e); }}
        isLoading={{
          log: isActionLoading(plan.plan_id, 'completed'),
          skip: isActionLoading(plan.plan_id, 'skipped'),
          favorite: false
        }}
      />
    );
  });

  var skippedCards = skippedMeals.map(function(plan) {
    var meal = getMealWithFallback(plan);
    var mealName = meal ? meal.name : plan.meal_name || 'this meal';
    return (
      <MealCard
        key={plan.plan_id}
        meal={meal}
        planMacros={plan.macros}
        mealType={plan.meal_type}
        planId={plan.plan_id}
        status="skipped"
        isFavorite={favHas(plan.meal_id)}
        variant="home"
        showLoggedBadge={false}
        onCardClick={function() { handleMealClick(plan); }}
        onLog={function(e) { updateMealStatus(plan.plan_id, 'completed', e); }}
        onRemove={function(e) { confirmRemoveSkippedMeal(plan.plan_id, mealName, e); }}
        onFavorite={function(e) { toggleFavorite(plan.meal_id, e); }}
        isLoading={{
          log: isActionLoading(plan.plan_id, 'completed'),
          remove: isActionLoading(plan.plan_id, 'remove'),
          favorite: false
        }}
      />
    );
  });

  return (
    <div className="min-h-screen pb-bottom-nav bg-background" data-testid="home-page">
      {/* `header-safe-top` provides env(safe-area-inset-top) + 8px — the single
          top offset for this screen. No extra spacer / no p-4 top needed. */}
      <div className="header-safe-top px-4 pb-4 space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-start justify-between"
        >
          <h1 className="font-heading font-bold text-2xl" data-testid="home-greeting">
            Hello, {user?.name?.split(' ')[0] || 'there'}
          </h1>
        </motion.div>

        {/* Today's Summary Card - consolidated progress view */}
        {!loading && (
          <TodaySummaryCard 
            profile={profile}
            todayPlan={todayPlan}
            mealDetails={mealDetails}
          />
        )}

        <AiLogJobsSection title="AI meal analysis" compact />

        {!loading && (
          <button
            onClick={function() { navigate('/sources'); }}
            data-testid="home-sources-link"
            className="block w-full text-center text-[11px] text-muted-foreground/70 hover:text-accent transition-colors -mt-2"
          >
            Calorie &amp; macro targets are estimates · Sources &amp; methodology
          </button>
        )}

        {/* Primary Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-3 gap-2"
          data-testid="home-primary-actions"
        >
          <Button
            onClick={handleLogFoodClick}
            data-testid="log-food-btn"
            className="h-12 rounded-xl bg-accent text-white font-semibold hover:bg-accent/90 shadow-sm text-sm px-2"
          >
            <Plus size={18} className="mr-1" />
            Log Food
          </Button>
          {/* Plan Food + Regenerate stay VISIBLE for Free users so planning
              stays discoverable; each click is gated (paywall for Free) before
              any launcher/generation starts. Backend enforcement unchanged. */}
          <Button
            onClick={handleOpenPlanLauncher}
            data-testid="plan-food-btn"
            className="h-12 rounded-xl bg-secondary text-foreground font-semibold hover:bg-secondary/80 shadow-sm border border-border/40 text-sm px-2"
          >
            <Calendar size={18} className="mr-1" />
            Plan Food
          </Button>
          <Button
            onClick={handleOpenRegenerateModal}
            data-testid="regenerate-today-btn"
            className="h-12 rounded-xl bg-secondary text-foreground font-semibold hover:bg-secondary/80 shadow-sm border border-border/40 text-sm px-2"
          >
            <RotateCcw size={18} className="mr-1" />
            Regenerate
          </Button>
        </motion.div>

        {!loading && loadError && (
          <LoadErrorCard error={loadError} onRetry={fetchData} title="We couldn't load today" testId="home-load-error" />
        )}

        {/* Logged foods — SHARED for Free + Pro. Logging is a free feature, so completed
            logs (including grouped AI / restaurant-basket meals as ONE card) are always
            visible, with macros + Undo/remove. Rendered ONCE (no Free/Pro duplication). */}
        {!loading && loggedMeals.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-3"
            data-testid="logged-section"
          >
            <p className="text-xs text-[#14D5F6] uppercase tracking-wide px-1 flex items-center gap-1">
              <Check size={12} />
              Logged ({loggedMeals.length})
            </p>
            {loggedCards}
          </motion.div>
        )}

        {/* FREE + nothing logged yet today: a small, normal empty state (same card
            style as Home's other empty states) with the same Log Food action as
            the primary button. Pro keeps its Today's Plan section below. Plan
            Food / Regenerate above stay visible and paywall-gated (unchanged). */}
        {!isPro && !loading && !loadError && loggedMeals.length === 0 && (
          <div className="bg-card border border-border/40 rounded-xl p-6 text-center" data-testid="free-empty-logged">
            <p className="text-muted-foreground mb-4">Nothing logged yet today. Start by logging a meal or snack.</p>
            <Button
              onClick={handleLogFoodClick}
              data-testid="free-empty-log-food-btn"
              className="rounded-full bg-accent text-white font-semibold hover:bg-accent/90"
            >
              <Plus size={18} className="mr-1" />
              Log Food
            </Button>
          </div>
        )}

        {/* Planning Section — Pro only (Still Planned, Add Meal, Skipped, empty state). */}
        {isPro && (
        <motion.div
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-4"
          data-testid="todays-plan-section"
        >
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-semibold text-lg">Today&apos;s Plan</h2>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : loadError ? null : todayPlan.length > 0 ? (
            <div className="space-y-4">
              {/* Still Planned Section */}
              {stillPlannedMeals.length > 0 && (
                <div className="space-y-3">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide px-1">
                    Still Planned ({stillPlannedMeals.length})
                  </p>
                  {plannedCards}
                  {/* Add Meal Button */}
                  <Button
                    onClick={handleAddMealClick}
                    variant="outline"
                    className="w-full h-11 rounded-xl border-dashed border-border/60 text-muted-foreground hover:text-foreground hover:border-accent/50"
                    data-testid="add-meal-btn"
                  >
                    <Plus size={18} className="mr-2" />
                    Add Meal
                  </Button>
                </div>
              )}
              
              {/* Show Add Meal button even if no planned meals yet */}
              {stillPlannedMeals.length === 0 && (loggedMeals.length > 0 || skippedMeals.length > 0) && (
                <div className="space-y-3">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide px-1">
                    Still Planned (0)
                  </p>
                  <Button
                    onClick={handleAddMealClick}
                    variant="outline"
                    className="w-full h-11 rounded-xl border-dashed border-border/60 text-muted-foreground hover:text-foreground hover:border-accent/50"
                    data-testid="add-meal-btn-empty"
                  >
                    <Plus size={18} className="mr-2" />
                    Add Meal
                  </Button>
                </div>
              )}

              {/* Skipped Section - at bottom with Log/Delete actions */}
              {skippedMeals.length > 0 && (
                <div className="space-y-2 pt-2 border-t border-border/20">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide px-1">
                    Skipped ({skippedMeals.length})
                  </p>
                  {skippedCards}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-card border border-border/40 rounded-xl p-6 text-center" data-testid="no-plan-today">
              <p className="text-muted-foreground mb-4">No meals planned for today</p>
              <Button
                onClick={function() { setShowPlanLauncher(true); }}
                data-testid="start-planning-btn"
                className="rounded-full bg-accent text-white font-semibold hover:bg-accent/90"
              >
                Start Planning
              </Button>
            </div>
          )}
        </motion.div>
        )}
      </div>

      {/* Remove Skipped Meal Confirmation Modal */}
      <AnimatePresence>
        {showRemoveConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={cancelRemove}
            data-testid="remove-confirm-overlay"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-background rounded-2xl w-full max-w-sm p-6 shadow-xl"
              onClick={function(e) { e.stopPropagation(); }}
              data-testid="remove-confirm-modal"
            >
              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
                  <AlertTriangle size={24} className="text-red-500" />
                </div>
              </div>
              
              {/* Title */}
              <h3 className="font-heading font-bold text-lg text-center mb-2">
                Remove from Plan?
              </h3>
              
              {/* Description */}
              <p className="text-sm text-muted-foreground text-center mb-6">
                Are you sure you would like to remove this meal from your plan?
              </p>
              
              {/* Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={cancelRemove}
                  variant="outline"
                  className="flex-1 h-11 rounded-xl"
                  data-testid="remove-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={executeRemoveSkippedMeal}
                  className="flex-1 h-11 rounded-xl bg-red-500 text-white hover:bg-red-600"
                  data-testid="remove-confirm-btn"
                >
                  Remove
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Plan Food Launcher Modal */}
      <PlanFoodLauncher 
        isOpen={showPlanLauncher}
        onClose={function() { setShowPlanLauncher(false); }}
        context="home"
        returnTo="/home"
      />
      
      {/* Regenerate Today's Plan Modal */}
      <AnimatePresence>
        {showRegenerateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={handleCloseRegenerateModal}
            data-testid="regenerate-modal-overlay"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-background rounded-2xl w-full max-w-sm p-6 shadow-xl relative"
              onClick={function(e) { e.stopPropagation(); }}
              data-testid="regenerate-modal"
            >
              {/* Close button */}
              <button
                onClick={handleCloseRegenerateModal}
                className="absolute top-4 right-4 text-muted-foreground hover:text-foreground z-10"
              >
                <X size={20} />
              </button>
              
              {/* Regenerate flow now opens directly into AI keep-selection
                  (or 'preferences' if today is empty). The legacy
                  Browse-vs-AI 'confirm' step was removed — Regenerate
                  always means AI regeneration. */}
              
              {/* Step 1.5: Keep Selections - choose meals to keep */}
              {regenerateStep === 'keep-selection' && (
                <>
                  <h3 className="font-heading font-bold text-lg text-center mb-2">
                    Keep any meals?
                  </h3>
                  <p className="text-sm text-muted-foreground text-center mb-4">
                    Select meals you want to keep. Unselected meals will be replaced. Linked leftover meals are kept together — use Replace both to change them.
                  </p>
                  
                  <div className="space-y-2 max-h-[280px] overflow-y-auto mb-4">
                    {todayPlan.filter(function(p) { return p.status === 'pending'; }).map(function(plan) {
                      var isKept = mealsToKeep.includes(plan.plan_id);
                      var linked = isLinkedRow(plan);
                      var details = mealDetails[plan.meal_id] || {};
                      var displayName = details.name || plan.meal_name || plan.name || 'Meal';
                      var displayImage = details.image_url || plan.image_url;
                      var displayCals = details.calories || plan.calories || 0;
                      var displayProtein = details.protein || plan.protein || 0;

                      // LINKED (cook/leftover) rows are protected: auto-kept, not
                      // individually deselectable. Replacement goes through the
                      // shared pair-safe Replace Both flow (same as PlanningHub).
                      if (linked) {
                        var roleLabel = plan.leftover_role === 'cook' ? 'Batch cook' : 'Leftover';
                        return (
                          <div
                            key={plan.plan_id}
                            className="w-full p-3 rounded-xl border-2 border-accent/40 bg-accent/5 flex items-center gap-3"
                            data-testid={'keep-meal-linked-' + plan.plan_id}
                          >
                            <div className="w-6 h-6 rounded-md border-2 border-accent bg-accent flex items-center justify-center">
                              <Lock size={13} className="text-white" />
                            </div>
                            {displayImage ? (
                              <MealImage src={displayImage} alt={displayName}
                                className="w-12 h-12 rounded-lg object-cover"
                                onError={function(e) { e.target.style.display = 'none'; }} />
                            ) : (
                              <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center">
                                <ChefHat size={20} className="text-muted-foreground" />
                              </div>
                            )}
                            <div className="flex-1 text-left min-w-0">
                              <p className="text-xs text-muted-foreground uppercase">
                                {plan.meal_type}
                                <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] bg-accent/10 text-accent">{roleLabel}</span>
                              </p>
                              <p className="font-medium text-sm line-clamp-1">{displayName}</p>
                              <p className="text-xs text-muted-foreground">Linked leftover — kept together</p>
                            </div>
                            <button
                              type="button"
                              onClick={function(e) { openHomePairSwap(plan, e); }}
                              className="shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-accent/10 text-accent hover:bg-accent/20 transition-colors"
                              data-testid={'keep-meal-replace-both-' + plan.plan_id}
                            >
                              <RefreshCw size={12} /> {plan.leftover_role === 'leftover' ? 'Replace' : 'Replace both'}
                            </button>
                          </div>
                        );
                      }

                      return (
                        <button
                          key={plan.plan_id}
                          onClick={function() { toggleMealToKeep(plan.plan_id); }}
                          className={'w-full p-3 rounded-xl border-2 transition-all flex items-center gap-3 ' + 
                            (isKept 
                              ? 'border-primary bg-primary/10' 
                              : 'border-transparent bg-secondary/30 hover:bg-secondary/50'
                            )
                          }
                          data-testid={'keep-meal-' + plan.plan_id}
                        >
                          <div className={'w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ' +
                            (isKept ? 'border-primary bg-primary' : 'border-muted-foreground/30')
                          }>
                            {isKept && <Check size={14} className="text-white" />}
                          </div>
                          {displayImage ? (
                            <MealImage 
                              src={displayImage} 
                              alt={displayName}
                              className="w-12 h-12 rounded-lg object-cover"
                              onError={function(e) { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center">
                              <ChefHat size={20} className="text-muted-foreground" />
                            </div>
                          )}
                          <div className="flex-1 text-left">
                            <p className="text-xs text-muted-foreground uppercase">{plan.meal_type}</p>
                            <p className="font-medium text-sm line-clamp-1">{displayName}</p>
                            <p className="text-xs text-muted-foreground">
                              {displayCals} cal • {displayProtein}g P
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  
                  <div className="text-center text-sm text-muted-foreground mb-4" data-testid="keep-summary-text">
                    {(function() {
                      // Drive the count from the SAME effective keep set used to
                      // build the request, so display always matches what is sent
                      // (includes protected linked leftovers, not just manual picks).
                      var effKeep = buildRegenSelection(todayPlan, mealsToKeep).mealsToKeep || [];
                      return effKeep.length === 0
                        ? 'All meals will be replaced'
                        : effKeep.length + ' meal' + (effKeep.length > 1 ? 's' : '') + ' will be kept';
                    })()}
                  </div>
                  
                  <Button
                    onClick={handleProceedFromKeepSelection}
                    className="w-full h-12 rounded-xl font-semibold"
                    data-testid="proceed-from-keep-btn"
                  >
                    Continue
                  </Button>
                  <Button
                    onClick={handleCloseRegenerateModal}
                    variant="ghost"
                    className="w-full h-10 rounded-xl text-muted-foreground mt-2"
                    data-testid="back-from-keep-btn"
                  >
                    Back
                  </Button>
                </>
              )}
              
              {/* Step 2: Before we start - preferences */}
              {regenerateStep === 'preferences' && (
                <>
                  <h3 className="font-heading font-bold text-lg text-center mb-2">
                    Before we start
                  </h3>
                  <p className="text-sm text-muted-foreground text-center mb-6">
                    Review your preferences or continue to generate
                  </p>
                  
                  <div className="space-y-3">
                    {/* Cooking Preferences */}
                    <button
                      onClick={handleEditCookingPrefs}
                      className="w-full p-4 bg-secondary/30 hover:bg-secondary/50 rounded-xl transition-colors"
                      data-testid="edit-cooking-btn"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                          <ChefHat size={20} className="text-muted-foreground" />
                        </div>
                        <div className="flex-1 text-left">
                          <p className="font-medium">Cooking Preferences</p>
                          <p className="text-xs text-muted-foreground">
                            {selectedCookware.length > 0 
                              ? selectedCookware.join(', ') + (selectedCookTime ? ' \u2022 ' + selectedCookTime + ' min max' : '')
                              : 'Not set - tap to configure'}
                          </p>
                        </div>
                        <Edit2 size={16} className="text-muted-foreground" />
                      </div>
                    </button>
                    
                    {/* My Pantry - editable */}
                    <button
                      onClick={handleEditPantry}
                      className="w-full p-4 bg-secondary/30 hover:bg-secondary/50 rounded-xl transition-colors"
                      data-testid="edit-pantry-btn"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                          <Package size={20} className="text-muted-foreground" />
                        </div>
                        <div className="flex-1 text-left">
                          <p className="font-medium">My Pantry</p>
                          <p className="text-xs text-muted-foreground">
                            {pantryItems.length > 0 
                              ? pantryItems.length + ' items - auto-prioritized'
                              : 'No items - tap to add'}
                          </p>
                        </div>
                        <Edit2 size={16} className="text-muted-foreground" />
                      </div>
                    </button>
                    
                    {/* Meal Filters */}
                    <div className="border border-border/40 rounded-xl overflow-hidden">
                      <button
                        onClick={function() { setShowFiltersPanel(!showFiltersPanel); }}
                        className="w-full p-4 bg-secondary/30 hover:bg-secondary/50 transition-colors"
                        data-testid="toggle-filters-btn"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                            <Filter size={20} className="text-muted-foreground" />
                          </div>
                          <div className="flex-1 text-left">
                            <p className="font-medium">Meal Filters</p>
                            <p className="text-xs text-muted-foreground">
                              {(selectedTags.length > 0 || selectedCuisine.length > 0)
                                ? (selectedTags.length > 0 ? selectedTags.length + ' tags' : '') + 
                                  (selectedTags.length > 0 && selectedCuisine.length > 0 ? ' • ' : '') +
                                  (selectedCuisine.length > 0 ? selectedCuisine.length + ' cuisine' : '')
                                : 'Filter by tags, cuisine (optional)'}
                            </p>
                          </div>
                          {showFiltersPanel ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </div>
                      </button>
                      
                      {showFiltersPanel && (
                        <div className="p-4 pt-2 border-t border-border/40 space-y-4">
                          {/* Tags */}
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                              <Tag size={12} /> Tags
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {['High-Protein', 'Low-Carb', 'Quick', 'Vegetarian', 'Gluten-Free', 'Dairy-Free'].map(function(tag) {
                                var isSelected = selectedTags.includes(tag);
                                return (
                                  <button
                                    key={tag}
                                    onClick={function() {
                                      if (isSelected) {
                                        setSelectedTags(selectedTags.filter(function(t) { return t !== tag; }));
                                      } else {
                                        setSelectedTags([].concat(selectedTags, [tag]));
                                      }
                                    }}
                                    className={'px-2.5 py-1 rounded-full text-xs font-medium transition-colors ' + 
                                      (isSelected ? 'bg-accent text-white' : 'bg-secondary/50 hover:bg-secondary/70')}
                                    data-testid={'tag-' + tag}
                                  >
                                    {isSelected && <Check size={10} className="inline mr-1" />}
                                    {tag}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          
                          {/* Cuisine */}
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-2">Cuisine</p>
                            <div className="flex flex-wrap gap-1.5">
                              {['Mexican', 'Italian', 'Asian', 'American', 'Mediterranean'].map(function(cuisine) {
                                var isSelected = selectedCuisine.includes(cuisine);
                                return (
                                  <button
                                    key={cuisine}
                                    onClick={function() {
                                      if (isSelected) {
                                        setSelectedCuisine(selectedCuisine.filter(function(c) { return c !== cuisine; }));
                                      } else {
                                        setSelectedCuisine([].concat(selectedCuisine, [cuisine]));
                                      }
                                    }}
                                    className={'px-2.5 py-1 rounded-full text-xs font-medium transition-colors ' + 
                                      (isSelected ? 'bg-purple-500 text-white' : 'bg-secondary/50 hover:bg-secondary/70')}
                                    data-testid={'cuisine-' + cuisine}
                                  >
                                    {isSelected && <Check size={10} className="inline mr-1" />}
                                    {cuisine}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          
                          {/* Clear filters */}
                          {(selectedTags.length > 0 || selectedCuisine.length > 0) && (
                            <button
                              onClick={function() { setSelectedTags([]); setSelectedCuisine([]); }}
                              className="text-xs text-muted-foreground hover:text-foreground"
                            >
                              Clear all filters
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <Button
                      onClick={handleProceedToGenerate}
                      className="w-full h-12 rounded-xl bg-accent text-white font-semibold hover:bg-accent/90 mt-4"
                      data-testid="proceed-generate-btn"
                    >
                      <Wand2 size={18} className="mr-2" />
                      Generate Today&apos;s Plan
                    </Button>
                    
                    <Button
                      onClick={function() {
                        // Back from preferences: return to keep-selection if
                        // there are still-plannable meals, otherwise close
                        // the modal entirely (the legacy 'confirm' step is
                        // gone).
                        var stillPlannable = todayPlan.filter(function(p) {
                          return p.status !== 'skipped' && p.status !== 'completed';
                        });
                        if (stillPlannable.length > 0) {
                          setRegenerateStep('keep-selection');
                        } else {
                          handleCloseRegenerateModal();
                        }
                      }}
                      variant="ghost"
                      className="w-full h-10 rounded-xl text-muted-foreground"
                    >
                      Back
                    </Button>
                  </div>
                </>
              )}
              
              {/* Step 3: Cooking Preferences Editor */}
              {regenerateStep === 'cooking' && (
                <div className="max-h-[70vh] overflow-y-auto -mx-2 px-2">
                  <CookingPreferencesSelector
                    selectedCookware={selectedCookware}
                    selectedCookTime={selectedCookTime}
                    onSave={handleSaveCookingPrefs}
                    onBack={function() { setRegenerateStep('preferences'); }}
                  />
                </div>
              )}
              
              {/* Step 3b: Pantry Editor */}
              {regenerateStep === 'pantry' && (
                <div className="max-h-[70vh] overflow-y-auto -mx-2 px-2">
                  <h3 className="font-heading font-bold text-lg text-center mb-2">My Pantry</h3>
                  <p className="text-sm text-muted-foreground text-center mb-4">
                    Select items you have available
                  </p>
                  <PantrySelector
                    selectedItems={pantryItems}
                    onItemsChange={setPantryItems}
                    onClose={function() { handleSavePantry(pantryItems); }}
                  />
                </div>
              )}
              
              {/* Step 4: Generating */}
              {regenerateStep === 'generating' && (
                <div className="py-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent/10 flex items-center justify-center">
                    <div className="animate-spin w-8 h-8 border-3 border-accent border-t-transparent rounded-full" />
                  </div>
                  <h3 className="font-heading font-bold text-lg mb-2">
                    Generating your plan...
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Finding the best meals for your day
                  </p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <BottomNav />

      {/* Shared pair-safe Replace Both dialog (same component as PlanningHub). */}
      <PairSwapDialog
        context={pairSwapContext}
        onClose={function() { setPairSwapContext(null); }}
        onSwapped={fetchData}
      />
    </div>
  );
}

export default Home;
